/**
 * SidebarPatientSearch.tsx
 *
 * Live "as-you-type" patient search for the top-of-sidebar Search Patients box,
 * matching the old FTP behavior. Reuses the same primitives as the shared
 * `PatientSearchInput` (searchPatients, useDebounce, portalled dropdown) but is
 * styled for the dark sidebar surface and navigates on select instead of
 * exposing a controlled "selected patient" — the sidebar box is a quick global
 * jump, not a form field.
 *
 * Behavior:
 *   - Click a result / Enter on a highlighted row → open that patient's chart
 *     via usePatientNavigation() (also records the recent-patients visit).
 *   - Enter with no row highlighted → /patients?search=<query> (list, all matches).
 *
 * `onNavigate` fires on both of those paths so the caller can dismiss the mobile
 * nav drawer once the chart or list has opened. It is a prop rather than a
 * `useSidebar()` read so this component still renders outside a sidebar provider.
 */
import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, X, Loader2, User } from 'lucide-react';
import { cn, formatDate, formatPhone } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { searchPatients } from '@/services/patients.service';
import { usePatientNavigation } from '@/components/ui/PatientLink';
import type { PatientSummary } from '@/types/patient.types';

function formatDob(dob: string | null): string {
  const formatted = formatDate(dob);
  return formatted === '—' ? 'DOB unknown' : `DOB ${formatted}`;
}

interface SidebarPatientSearchProps {
  /** Called after a selection navigates away. Used to close the mobile drawer. */
  onNavigate?: () => void;
}

export function SidebarPatientSearch({ onNavigate }: SidebarPatientSearchProps = {}) {
  const navigate = useNavigate();
  const navToPatient = usePatientNavigation();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PatientSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const debouncedQuery = useDebounce(query, 300);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);

  // Anchor the dropdown to the input with fixed positioning so it escapes the
  // sidebar's overflow clipping. The sidebar input is narrow, so give results a
  // minimum width and clamp to the viewport so rows aren't cramped or off-screen.
  const updateDropdownPosition = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const width = Math.max(rect.width, 288);
    const maxLeft = window.innerWidth - width - 8;
    const left = Math.min(rect.left, Math.max(8, maxLeft));
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left,
      width,
      zIndex: 9999,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updateDropdownPosition();
  }, [open, results, updateDropdownPosition]);

  // ─── Fetch results ───────────────────────────────────────────────────

  useEffect(() => {
    const q = debouncedQuery.trim();
    if (!q) {
      setResults([]);
      setOpen(false);
      // A request cancelled mid-flight skips its own finally, so this branch owns
      // the reset — without it the spinner stays up for the rest of the session.
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchResults() {
      setLoading(true);
      try {
        const data = await searchPatients(q);
        if (!cancelled) {
          setResults(data);
          setOpen(data.length > 0);
          setActiveIndex(-1);
        }
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void fetchResults();

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  // ─── Close on outside click; reposition on scroll ───────────────────

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    }
    function handleScroll(e: Event) {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      updateDropdownPosition();
    }
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [updateDropdownPosition]);

  // ─── Handlers ────────────────────────────────────────────────────────

  const reset = useCallback(() => {
    setQuery('');
    setResults([]);
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  const handleSelect = useCallback(
    (patient: PatientSummary) => {
      navToPatient({
        patientId: patient.patientId,
        name: patient.displayName,
        photoUrl: patient.photoUrl,
      });
      reset();
      // Not inside reset(): the clear "X" button shares it and must not dismiss
      // the drawer.
      onNavigate?.();
    },
    [navToPatient, reset, onNavigate],
  );

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case 'ArrowDown':
        if (!open) return;
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, results.length - 1));
        break;
      case 'ArrowUp':
        if (!open) return;
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Enter': {
        e.preventDefault();
        if (open && activeIndex >= 0 && results[activeIndex]) {
          handleSelect(results[activeIndex]);
          return;
        }
        // No row highlighted → fall back to the full list search.
        const q = query.trim();
        if (q) {
          navigate(`/patients?search=${encodeURIComponent(q)}`);
          reset();
          onNavigate?.();
        }
        break;
      }
      case 'Escape':
        setOpen(false);
        break;
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div ref={containerRef} className="relative">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
      <input
        ref={inputRef}
        type="text"
        name="patient-search"
        autoComplete="off"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => results.length > 0 && setOpen(true)}
        onClick={() => results.length > 0 && !open && setOpen(true)}
        placeholder="Search Patients"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-haspopup="listbox"
        className="flex h-8 w-full rounded-md border border-white/10 bg-white/5 pl-8 pr-8 py-1 text-xs text-white placeholder:text-white/30 transition-colors focus:bg-white/10 focus:outline-none focus:ring-1 focus:ring-white/20"
      />
      {loading ? (
        /* `animate-spin` overwrites `transform`, so center with margin-auto rather
           than -translate-y-1/2, which the animation drops (drifting the icon down). */
        <Loader2 className="pointer-events-none absolute right-2.5 inset-y-0 my-auto h-3.5 w-3.5 animate-spin text-white/40" />
      ) : query ? (
        <button
          type="button"
          onClick={reset}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-white/40 hover:text-white/80 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}

      {/* Dropdown — portalled to document.body to escape sidebar overflow clipping */}
      {open && results.length > 0 && createPortal(
        <ul
          ref={dropdownRef}
          role="listbox"
          aria-label="Patient search results"
          style={dropdownStyle}
          className="max-h-72 overflow-y-auto rounded-md border border-slate-700 bg-[hsl(222,47%,16%)] p-1 shadow-xl"
        >
          {results.map((patient, index) => (
            <li
              key={patient.patientId}
              role="option"
              aria-selected={index === activeIndex}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(patient);
              }}
              onMouseEnter={() => setActiveIndex(index)}
              className={cn(
                'flex cursor-pointer items-center gap-2.5 rounded px-2.5 py-1.5 transition-colors',
                index === activeIndex
                  ? 'bg-white/15 text-white'
                  : 'text-white/80 hover:bg-white/10 hover:text-white',
              )}
            >
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white/10">
                <User className="h-3.5 w-3.5 text-white/60" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">
                  {patient.displayName}
                  {patient.displayId ? (
                    <span className="ml-2 font-mono text-xs font-normal text-white/50">
                      #{patient.displayId}
                    </span>
                  ) : null}
                </p>
                <p className="text-xs text-white/50">
                  {formatDob(patient.dateOfBirth)}
                  {patient.cellPhone ? (
                    <span className="ml-2">{formatPhone(patient.cellPhone)}</span>
                  ) : null}
                </p>
                {patient.email ? (
                  <p className="truncate text-xs text-white/40">{patient.email}</p>
                ) : null}
              </div>
            </li>
          ))}
        </ul>,
        document.body,
      )}
    </div>
  );
}
